import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, Zap, Trophy, Users, FileText, Dumbbell, BookOpen, 
  Clock, Lightbulb, ChevronRight, Sparkles, Heart, Crown, 
  Bot, BarChart2, Calendar, Briefcase, Target, X, Plus, Check,
  CheckSquare, Database, Layers, TrendingUp, PhoneOff, Shield,
  Award, Timer, CheckCircle, Activity, Coffee, Globe, Medal,
  MessageCircle, Star, Gift, ArrowUp, Share2, Flame, UserPlus, Lock,
  Rocket, CalendarIcon, Bell
} from 'lucide-react';
import { 
  PieChart, Pie, BarChart, Bar, XAxis, Tooltip, 
  Cell, ResponsiveContainer, LineChart, Line, YAxis,
  AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, 
  PolarRadiusAxis, Radar
} from 'recharts';
import TabNavigation from '../TabNavigation';
import DisciplineProgressChart from '../DigitalDiscipline/DisciplineProgressChart';
import SocialStatsComponent from './SocialStatsComponent';
import DisciplineStatsPanel from './DisciplineStatsPanel';
import HealthStatsComponent from './HealthStatsComponent';
import ProductivityStatsComponent from './ProductivityStatsComponent';
import SecurityStatsComponent from './SecurityStatsComponent';
import AchievementsComponent from './AchievementsComponent';
import DigitalDisciplineStatsIntegration from '../DigitalDiscipline/DigitalDisciplineStatsIntegration';
import MentalDevelopmentStats from './MentalDevelopmentStats';
import PhysicalActivityStats from './PhysicalActivityStats';
import SocialConnectionStats from './SocialConnectionStats';
import StatCard from './StatCard';
import { calculateLevel } from '../../utils/xpEngine';
import { useDisciplineStore } from '../../contexts/disciplineStore';

// Animation variants for smooth transitions
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Ana bileşen düzenlemesi - DigitalDisciplineStats ekledim
const StatisticsPanel = ({ isDarkMode = true, initialTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  
  return (
    <div className={`statistics-panel pb-20 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
      <TabNavigation
        tabs={[
          { id: 'overview', label: 'Genel Bakış', icon: <BarChart2 size={16} /> },
          { id: 'digital-discipline', label: 'Dijital Disiplin', icon: <Shield size={16} /> },
          { id: 'mental', label: 'Mental Gelişim', icon: <Brain size={16} /> },
          { id: 'physical', label: 'Fiziksel Aktivite', icon: <Dumbbell size={16} /> },
          { id: 'social', label: 'Sosyal Bağlantı', icon: <Users size={16} /> },
          { id: 'health', label: 'Sağlık', icon: <Heart size={16} /> },
          { id: 'productivity', label: 'Üretkenlik', icon: <Target size={16} /> },
          { id: 'security', label: 'Güvenlik', icon: <Lock size={16} /> },
          { id: 'journal', label: 'Yazılarım', icon: <FileText size={16} /> },
          { id: 'qr-connections', label: 'Bağlantılarım', icon: <Globe size={16} /> },
          { id: 'social-stats', label: 'Sosyal İstatistikler', icon: <MessageCircle size={16} /> },
          { id: 'achievements', label: 'Başarımlar', icon: <Trophy size={16} /> }
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        isDarkMode={isDarkMode}
      />
      
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <div className="space-y-6">
              {/* Main Stats cards */}
              <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 sm:grid-cols-4 gap-4"
              >
                <StatCard 
                  title="Mental Gelişim" 
                  icon={<Brain />} 
                  value="87" 
                  unit="XP" 
                  progress={87} 
                  trend={12}
                  color="#00C2FF"
                  index={0}
                  isDarkMode={isDarkMode}
                  onClick={() => setActiveTab('mental')}
                />
                
                <StatCard 
                  title="Fiziksel Aktivite" 
                  icon={<Dumbbell />} 
                  value="64" 
                  unit="XP" 
                  progress={64}
                  trend={8}
                  color="#FF6B6B"
                  index={1}
                  isDarkMode={isDarkMode}
                  onClick={() => setActiveTab('physical')}
                />
                
                <StatCard 
                  title="Sosyal Bağlantı" 
                  icon={<Users />} 
                  value="73" 
                  unit="XP" 
                  progress={73}
                  trend={-4}
                  isGrowing={false}
                  color="#FFA400"
                  index={2}
                  isDarkMode={isDarkMode}
                  onClick={() => setActiveTab('social')}
                />
                
                <StatCard 
                  title="Dijital Disiplin" 
                  icon={<Shield />} 
                  value="92" 
                  unit="XP" 
                  progress={92}
                  trend={15}
                  color="#00FFD1"
                  index={3}
                  isDarkMode={isDarkMode}
                  onClick={() => setActiveTab('digital-discipline')}
                />
              </motion.div>
              
              {/* Başarımlar bölümü - Yeni Eklendi */}
              <motion.div
                className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
                variants={itemVariants}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold flex items-center">
                    <Trophy className="h-5 w-5 mr-2 text-amber-500" /> 
                    Başarımlar
                  </h3>
                  <button className={`text-xs px-3 py-1 rounded-full ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
                    Tümünü Gör
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Son kazanılan başarımlar */}
                  <div className={`p-3 rounded-lg border-l-4 ${isDarkMode ? 'bg-gray-700 border-green-500' : 'bg-gray-100 border-green-500'}`}>
                    <div className="flex">
                      <div className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} mr-3`}>
                        <Users size={18} className="text-green-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm">Sosyal Kelebek</h4>
                        <p className="text-xs opacity-70">23 Nisan 2025</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`p-3 rounded-lg border-l-4 ${isDarkMode ? 'bg-gray-700 border-blue-500' : 'bg-gray-100 border-blue-500'}`}>
                    <div className="flex">
                      <div className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} mr-3`}>
                        <Timer size={18} className="text-blue-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm">Zaman Ustası</h4>
                        <p className="text-xs opacity-70">18 Nisan 2025</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={`p-3 rounded-lg border-l-4 ${isDarkMode ? 'bg-gray-700 border-purple-500' : 'bg-gray-100 border-purple-500'}`}>
                    <div className="flex">
                      <div className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} mr-3`}>
                        <Brain size={18} className="text-purple-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm">Düşünce Lideri</h4>
                        <p className="text-xs opacity-70">5 Nisan 2025</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Başarım ilerleme çubuğu */}
                <div className="mt-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Toplam Başarım İlerlemesi</span>
                    <span>16/30</span>
                  </div>
                  <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-amber-500"
                      initial={{ width: 0 }}
                      animate={{ width: '53%' }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                    />
                  </div>
                </div>
              </motion.div>
              
              {/* Aktivite özeti */}
              <motion.div
                className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
                variants={itemVariants}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold flex items-center">
                    <Activity className="h-5 w-5 mr-2 text-blue-500" /> 
                    Genel Aktivite
                  </h3>
                  <div className="flex space-x-1">
                    <button className={`text-xs px-2 py-1 rounded-l-full ${isDarkMode ? 'bg-blue-500 text-white' : 'bg-blue-500 text-white'}`}>
                      Haftalık
                    </button>
                    <button className={`text-xs px-2 py-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
                      Aylık
                    </button>
                    <button className={`text-xs px-2 py-1 rounded-r-full ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>
                      Yıllık
                    </button>
                  </div>
                </div>
                
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={[
                      { day: 'Pzt', value: 65 },
                      { day: 'Sal', value: 78 },
                      { day: 'Çar', value: 52 },
                      { day: 'Per', value: 95 },
                      { day: 'Cum', value: 87 },
                      { day: 'Cmt', value: 43 },
                      { day: 'Paz', value: 28 },
                    ]}>
                      <XAxis dataKey="day" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                          borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                        }}
                      />
                      <Line 
                        type="monotone"
                        dataKey="value"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ fill: '#3b82f6' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}        {activeTab === 'digital-discipline' && (
          <motion.div
            key="digital-discipline"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <DisciplineStatsPanel 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}{activeTab === 'mental' && (
          <motion.div
            key="mental"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <MentalDevelopmentStats 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}

        {activeTab === 'physical' && (
          <motion.div
            key="physical"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <PhysicalActivityStats 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}

        {activeTab === 'social' && (
          <motion.div
            key="social"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <SocialConnectionStats 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}        {activeTab === 'health' && (
          <motion.div
            key="health"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <HealthStatsComponent 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}        {activeTab === 'productivity' && (
          <motion.div
            key="productivity"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <ProductivityStatsComponent 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}        {activeTab === 'security' && (
          <motion.div
            key="security"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <SecurityStatsComponent 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}

        {activeTab === 'journal' && (
          <motion.div
            key="journal"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="text-lg font-bold mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-500" />
                Yazılarım
              </h3>
              <p className="text-gray-500">Günlük ve yazı istatistikleri burada görünecek.</p>
            </div>
          </motion.div>
        )}

        {activeTab === 'qr-connections' && (
          <motion.div
            key="qr-connections"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="text-lg font-bold mb-4 flex items-center">
                <Globe className="h-5 w-5 mr-2 text-green-500" />
                Bağlantılarım
              </h3>
              <p className="text-gray-500">QR kod bağlantı istatistikleri burada görünecek.</p>
            </div>
          </motion.div>
        )}        {activeTab === 'social-stats' && (
          <motion.div
            key="social-stats"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <SocialStatsComponent 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}        {activeTab === 'achievements' && (
          <motion.div
            key="achievements"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <AchievementsComponent 
              isDarkMode={isDarkMode} 
              onBackToOverview={() => setActiveTab('overview')}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StatisticsPanel;
